import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AiDebugService, ChromaChunk, EndpointAvailability } from './ai-debug.service';
import { AiSemanticSearchResult, AiSemanticSearchTestService } from './ai-semantic-search-test.service';

// ── Domain interfaces ─────────────────────────────────────────────────────────

interface StatusBadge {
  label: string;
  severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary';
}

interface ParsedChunk {
  id: string;
  chunkIndex: number;
  chunkIndexInSection: number | null;
  sectionId: string;
  noteId: string;
  noteTitle: string;
  contentHash: string;
  chunkType: string;
  preview: string;
  fullText: string;
  tokenCount: number | null;
  textLength: number | null;
  sourceTextLength: number | null;
  headingPath: string;
  noteStatus: string;
  noteCreated: string;
  noteLastModified: string;
  noteReferenceDate: string;
  noteKeywords: string;
  noteAiKeywords: string;
  noteTags: string[];
  embeddingModel: string;
  embeddingDimension: number | null;
  schemaVersion: string;
  strategyVersion: string;
  indexedAt: string;
  normalizedTerms: string[];
  metadata: Record<string, unknown>;
  raw: Record<string, unknown>;
  statusBadges: StatusBadge[];
}

interface NoteOverview {
  noteId: string;
  noteTitle: string;
  chunkCount: number;
  totalTokens: number;
  embeddingModel: string;
  embeddingDimension: number | null;
  schemaVersion: string;
  strategyVersion: string;
  lastIndexedAt: string;
  noteLastModified: string;
  noteReferenceDate: string;
}

interface IndexWarning {
  message: string;
  severity: 'warn' | 'danger' | 'info';
}

interface RetrievalResult {
  rank: number;
  score: number | null;
  noteId: string;
  noteTitle: string;
  headingPath: string;
  tokenCount: number | null;
  preview: string;
  isCurrentNote: boolean;
  statusBadges: StatusBadge[];
  fullText: string;
  metadata: Record<string, unknown>;
  raw: Record<string, unknown>;
}

interface SemanticResult {
  rank: number;
  noteId: string;
  noteTitle: string;
  score: number | null;
  snippet: string;
  raw: Record<string, unknown>;
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-ai-index-inspector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    ChipModule,
    InputTextModule,
    PanelModule,
    RippleModule,
    TableModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './ai-index-inspector.component.html',
})
export class AiIndexInspectorComponent {
  private readonly aiDebug = inject(AiDebugService);
  private readonly searchSvc = inject(AiSemanticSearchTestService);
  activeTab: 'overview' | 'chunks' | 'retrieval' | 'semantic' | 'raw' = 'overview';

  // ── Shared ────────────────────────────────────────────────────────────────────
  noteId = '';
  endpointAvailability: EndpointAvailability = this.aiDebug.getEndpointAvailability();

  // ── Note inspection ────────────────────────────────────────────────────────────
  noteLoading = false;
  noteError = '';
  overview: NoteOverview | null = null;
  chunks: ParsedChunk[] = [];
  warnings: IndexWarning[] = [];
  rawNoteData: unknown = null;
  createEmbeddingsLoading = false;
  deleteEmbeddingsLoading = false;
  embeddingsMessage = '';

  // ── Chunks tab ─────────────────────────────────────────────────────────────────
  termFilter: 'all' | 'no-short' | 'no-numbers' | 'no-urls' | 'dates-only' = 'all';

  // ── Retrieval Test ─────────────────────────────────────────────────────────────
  retrievalQuery = '';
  retrievalK: number | null = 10;
  retrievalLoading = false;
  retrievalError = '';
  retrievalResults: RetrievalResult[] = [];
  rawRetrievalData: unknown = null;

  // ── Semantic Query ─────────────────────────────────────────────────────────────
  semanticQuery = '';
  semanticK: number | null = 5;
  semanticLoading = false;
  semanticError = '';
  semanticResults: SemanticResult[] = [];
  rawSemanticData: unknown = null;

  // ── Copy feedback ──────────────────────────────────────────────────────────────
  recentlyCopied = new Set<string>();

  // ── Thresholds ─────────────────────────────────────────────────────────────────
  readonly LARGE_CHUNK_TOKENS = 500;
  readonly SMALL_CHUNK_TOKENS = 40;

  // ── Tab management ────────────────────────────────────────────────────────────

  setTab(tab: 'overview' | 'chunks' | 'retrieval' | 'semantic' | 'raw'): void {
    this.activeTab = tab;
  }

  // ── Note inspection ────────────────────────────────────────────────────────────

  inspectNote(): void {
    const id = this.noteId.trim();
    if (!id) {
      this.noteError = 'Please enter a note ID.';
      return;
    }

    this.noteLoading = true;
    this.noteError = '';
    this.embeddingsMessage = '';
    this.overview = null;
    this.chunks = [];
    this.warnings = [];
    this.rawNoteData = null;

    this.aiDebug
      .getNoteDebug(id)
      .pipe(finalize(() => (this.noteLoading = false)))
      .subscribe({
        next: (result) => {
          this.rawNoteData = result.raw;
          this.chunks = this.parseChunks(result.chunks);
          this.overview = this.buildOverview(id, this.chunks);
          this.warnings = this.buildWarnings(this.overview, this.chunks);
        },
        error: (err) => {
          const statusCode = Number((err as any)?.status ?? 0);
          if (statusCode === 503 || statusCode === 504) {
            this.noteError = 'AI backend is temporarily unavailable. Please retry in a few moments.';
          } else {
            this.noteError =
              (err as any)?.error?.message || (err as any)?.error?.error || (err as any)?.message || 'Note debug failed.';
          }
        },
      });
  }

  createEmbeddings(): void {
    const id = this.noteId.trim();
    if (!id || this.createEmbeddingsLoading) return;
    this.createEmbeddingsLoading = true;
    this.embeddingsMessage = '';
    this.aiDebug
      .reindexSingleNote(id)
      .pipe(finalize(() => (this.createEmbeddingsLoading = false)))
      .subscribe({
        next: () => {
          this.embeddingsMessage = 'Create embeddings request submitted.';
        },
        error: (err) => {
          this.embeddingsMessage =
            (err as any)?.error?.message || (err as any)?.message || 'Create embeddings failed.';
        },
      });
  }

  deleteEmbeddings(): void {
    const id = this.noteId.trim();
    if (!id || this.deleteEmbeddingsLoading || !this.endpointAvailability.canDeleteEmbeddingsForNote) return;
    this.deleteEmbeddingsLoading = true;
    this.embeddingsMessage = '';
    this.aiDebug
      .deleteSingleNoteFromIndex(id)
      .pipe(finalize(() => (this.deleteEmbeddingsLoading = false)))
      .subscribe({
        next: () => {
          this.embeddingsMessage = 'Delete embeddings request completed.';
        },
        error: (err) => {
          this.embeddingsMessage =
            (err as any)?.error?.message || (err as any)?.message || 'Delete embeddings failed.';
        },
      });
  }

  // ── Retrieval Test ────────────────────────────────────────────────────────────

  runRetrievalTest(): void {
    const question = this.retrievalQuery.trim();
    if (!question) {
      this.retrievalError = 'Please enter a question.';
      return;
    }

    this.retrievalLoading = true;
    this.retrievalError = '';
    this.retrievalResults = [];
    this.rawRetrievalData = null;

    const payload: { question: string; k?: number } = { question };
    if (this.retrievalK != null && this.retrievalK > 0) {
      payload.k = Math.floor(this.retrievalK);
    }

    this.aiDebug
      .runRetrievalTest(payload)
      .pipe(finalize(() => (this.retrievalLoading = false)))
      .subscribe({
        next: (res) => {
          const data = (res?.data ?? {}) as Record<string, unknown>;
          this.rawRetrievalData = data;
          const rawArr = this.readArrayField(data, ['raw_results', 'reranked_results', 'raw', 'retrieved']);
          this.retrievalResults = rawArr.map((item, i) => this.parseRetrievalResult(item, i + 1));
        },
        error: (err) => {
          this.retrievalError =
            (err as any)?.error?.message || (err as any)?.error?.error || (err as any)?.message || 'Retrieval test failed.';
        },
      });
  }

  // ── Semantic Query ────────────────────────────────────────────────────────────

  runSemanticQuery(): void {
    const q = this.semanticQuery.trim();
    if (!q) {
      this.semanticError = 'Please enter a query.';
      return;
    }

    this.semanticLoading = true;
    this.semanticError = '';
    this.semanticResults = [];
    this.rawSemanticData = null;

    const payload: { query_text: string; top_k?: number; prefer_recent?: boolean } = {
      query_text: q,
      prefer_recent: true,
    };
    if (this.semanticK != null && this.semanticK > 0) {
      payload.top_k = this.semanticK;
    }

    this.searchSvc
      .searchNotes(payload)
      .pipe(finalize(() => (this.semanticLoading = false)))
      .subscribe({
        next: (res) => {
          this.rawSemanticData = res?.data ?? null;
          const results = Array.isArray(res?.data?.results) ? res.data!.results! : [];
          this.semanticResults = results.map((r, i) => this.parseSemanticResult(r, i + 1));
        },
        error: (err) => {
          this.semanticError =
            (err as any)?.error?.message || (err as any)?.message || 'Semantic search failed.';
        },
      });
  }

  // ── Clipboard ─────────────────────────────────────────────────────────────────

  copyToClipboard(text: string, key: string): void {
    navigator.clipboard.writeText(text).catch(() => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* ignore */ }
    });
    this.recentlyCopied.add(key);
    setTimeout(() => this.recentlyCopied.delete(key), 2000);
  }

  // ── Terms helpers ─────────────────────────────────────────────────────────────

  getFilteredTerms(chunk: ParsedChunk): string[] {
    return this.filterTerms(chunk.normalizedTerms, this.termFilter);
  }

  private filterTerms(terms: string[], mode: string): string[] {
    switch (mode) {
      case 'no-short': return terms.filter(t => t.length >= 4);
      case 'no-numbers': return terms.filter(t => !/^\d+$/.test(t));
      case 'no-urls': return terms.filter(t => !t.startsWith('http'));
      case 'dates-only': return terms.filter(t => /\d{4}/.test(t));
      default: return terms;
    }
  }

  // ── Diagnostics ───────────────────────────────────────────────────────────────

  private isStaleIndex(chunk: ParsedChunk): boolean {
    if (!chunk.indexedAt || !chunk.noteLastModified) return false;
    const a = new Date(chunk.indexedAt).getTime();
    const b = new Date(chunk.noteLastModified).getTime();
    return Number.isFinite(a) && Number.isFinite(b) && a < b;
  }

  private isLargeChunk(chunk: ParsedChunk): boolean {
    return chunk.tokenCount != null && chunk.tokenCount > this.LARGE_CHUNK_TOKENS;
  }

  private isSmallChunk(chunk: ParsedChunk): boolean {
    return chunk.tokenCount != null && chunk.tokenCount < this.SMALL_CHUNK_TOKENS;
  }

  private hasMissingMetadata(chunk: ParsedChunk): boolean {
    return !chunk.noteTitle || !chunk.sectionId || !chunk.contentHash || !chunk.embeddingModel;
  }

  private getChunkStatusBadges(chunk: ParsedChunk): StatusBadge[] {
    const badges: StatusBadge[] = [];
    if (this.isStaleIndex(chunk)) badges.push({ label: 'Stale', severity: 'warn' });
    if (this.isLargeChunk(chunk)) badges.push({ label: 'Large', severity: 'warn' });
    if (this.isSmallChunk(chunk)) badges.push({ label: 'Small', severity: 'info' });
    if (this.hasMissingMetadata(chunk)) badges.push({ label: 'Missing Meta', severity: 'warn' });
    if (badges.length === 0) badges.push({ label: 'OK', severity: 'success' });
    return badges;
  }

  // ── Parse helpers ─────────────────────────────────────────────────────────────

  private parseChunks(chromaChunks: ChromaChunk[]): ParsedChunk[] {
    return chromaChunks.map((chunk, i) => this.parseChunk(chunk, i));
  }

  // Converts a service-parsed ChromaChunk (which already has heading and textLength correctly
  // extracted) into our richer ParsedChunk with diagnostics metadata.
  private parseChunk(chunk: ChromaChunk, index: number): ParsedChunk {
    const meta = chunk.metadata;
    // Fall back to metadata text fields in case the service couldn't extract the document body.
    const text = chunk.document
      || this.str(meta['text'] ?? meta['chunk_text'] ?? meta['content'] ?? meta['document_preview'] ?? '');
    const preview = text.length > 280 ? text.slice(0, 280) + '…' : text;

    const parsed: ParsedChunk = {
      id: chunk.id,
      chunkIndex: chunk.chunkIndex ?? index,
      chunkIndexInSection: this.num(meta['chunk_index_in_section'] ?? meta['chunkIndexInSection']),
      sectionId: this.str(meta['section_id'] ?? meta['sectionId']),
      noteId: this.str(meta['note_id'] ?? meta['noteId']),
      noteTitle: this.str(meta['note_title'] ?? meta['noteTitle']),
      contentHash: this.str(meta['content_hash'] ?? meta['contentHash']),
      chunkType: this.str(meta['chunk_type'] ?? meta['chunkType']),
      preview,
      fullText: text,
      tokenCount: this.num(meta['token_count'] ?? meta['tokenCount']),
      textLength: chunk.textLength || text.length,
      sourceTextLength: this.num(meta['source_text_length'] ?? meta['sourceTextLength']),
      // Use the pre-parsed heading from ChromaChunk, fall back to metadata heading_path
      headingPath: chunk.heading || this.str(meta['heading_path'] ?? meta['headingPath'] ?? meta['heading']),
      noteStatus: this.str(meta['note_status'] ?? meta['Status'] ?? meta['status']),
      noteCreated: this.str(meta['note_created'] ?? meta['created_on'] ?? meta['createdOn']),
      noteLastModified: this.str(meta['note_last_modified'] ?? meta['last_modified'] ?? meta['lastModified']),
      noteReferenceDate: this.str(
        meta['note_reference_date'] ?? meta['reference_date'] ?? meta['ReferenceDate'],
      ),
      noteKeywords: this.str(meta['note_keywords'] ?? meta['Keywords'] ?? meta['keywords']),
      noteAiKeywords: this.str(meta['note_ai_keywords'] ?? meta['AiKeywords'] ?? meta['ai_keywords']),
      noteTags: this.strArray(meta['note_tags'] ?? meta['Tags'] ?? meta['tags']),
      embeddingModel: this.str(meta['embedding_model'] ?? meta['model'] ?? meta['embeddingModel']),
      embeddingDimension: this.num(
        meta['embedding_dimensions'] ?? meta['embedding_dimension'] ?? meta['embeddingDimension'],
      ),
      schemaVersion: this.str(meta['schema_version'] ?? meta['schemaVersion']),
      strategyVersion: this.str(meta['strategy_version'] ?? meta['strategyVersion']),
      indexedAt: this.str(
        meta['indexed_at'] ?? meta['indexedAt'] ?? meta['created_at'] ?? meta['timestamp'],
      ),
      normalizedTerms: this.parseNormalizedTerms(meta['normalized_terms'] ?? meta['normalizedTerms']),
      metadata: meta,
      raw: chunk as unknown as Record<string, unknown>,
      statusBadges: [],
    };

    parsed.statusBadges = this.getChunkStatusBadges(parsed);
    return parsed;
  }

  private buildOverview(noteId: string, chunks: ParsedChunk[]): NoteOverview {
    const first = chunks[0];
    const totalTokens = chunks.reduce((sum, c) => sum + (c.tokenCount ?? 0), 0);
    const lastIndexedAt = chunks.reduce((latest, c) => {
      if (!c.indexedAt) return latest;
      return !latest || c.indexedAt > latest ? c.indexedAt : latest;
    }, '');

    return {
      noteId,
      noteTitle: first?.noteTitle || '',
      chunkCount: chunks.length,
      totalTokens,
      embeddingModel: first?.embeddingModel || '',
      embeddingDimension: first?.embeddingDimension ?? null,
      schemaVersion: first?.schemaVersion || '',
      strategyVersion: first?.strategyVersion || '',
      lastIndexedAt,
      noteLastModified: first?.noteLastModified || '',
      noteReferenceDate: first?.noteReferenceDate || '',
    };
  }

  private buildWarnings(overview: NoteOverview, chunks: ParsedChunk[]): IndexWarning[] {
    const warnings: IndexWarning[] = [];

    if (chunks.length === 0) {
      warnings.push({ message: 'No chunks found — note may not be indexed.', severity: 'danger' });
      return warnings;
    }

    const staleCount = chunks.filter(c => this.isStaleIndex(c)).length;
    if (staleCount > 0) {
      warnings.push({
        message: `${staleCount} chunk(s) are stale (indexed before the note was last modified).`,
        severity: 'warn',
      });
    }

    const largeCount = chunks.filter(c => this.isLargeChunk(c)).length;
    if (largeCount > 0) {
      warnings.push({
        message: `${largeCount} chunk(s) exceed ${this.LARGE_CHUNK_TOKENS} tokens — may reduce retrieval quality.`,
        severity: 'warn',
      });
    }

    const smallCount = chunks.filter(c => this.isSmallChunk(c)).length;
    if (smallCount > 0) {
      warnings.push({
        message: `${smallCount} very small chunk(s) (< ${this.SMALL_CHUNK_TOKENS} tokens).`,
        severity: 'info',
      });
    }

    const missingMetaCount = chunks.filter(c => this.hasMissingMetadata(c)).length;
    if (missingMetaCount > 0) {
      warnings.push({
        message: `${missingMetaCount} chunk(s) have incomplete metadata (missing title, section ID, content hash, or embedding model).`,
        severity: 'warn',
      });
    }

    const models = new Set(chunks.map(c => c.embeddingModel).filter(Boolean));
    if (models.size > 1) {
      warnings.push({
        message: `Mixed embedding models detected: ${[...models].join(', ')}.`,
        severity: 'danger',
      });
    }

    if (warnings.length === 0) {
      warnings.push({ message: 'All checks passed — index looks healthy.', severity: 'info' });
    }

    return warnings;
  }

  private parseRetrievalResult(item: unknown, rank: number): RetrievalResult {
    const raw = this.isRecord(item) ? item : {};
    const meta: Record<string, unknown> = this.isRecord(raw['metadata'])
      ? (raw['metadata'] as Record<string, unknown>)
      : {};
    const score = this.num(
      raw['final_score'] ?? raw['semantic_score'] ?? raw['score'] ?? raw['distance'],
    );
    const text = String(raw['snippet'] ?? raw['document'] ?? meta['document'] ?? '');
    const noteId = this.str(raw['note_id'] ?? meta['note_id'] ?? raw['id']);

    const result: RetrievalResult = {
      rank,
      score,
      noteId,
      noteTitle: this.str(raw['title'] ?? meta['note_title']),
      headingPath: this.str(meta['heading_path'] ?? meta['heading']),
      tokenCount: this.num(meta['token_count']),
      preview: text.length > 320 ? text.slice(0, 320) + '…' : text,
      isCurrentNote:
        !!this.noteId &&
        !!noteId &&
        (noteId === this.noteId ||
          noteId.includes(this.noteId) ||
          this.noteId.includes(noteId)),
      statusBadges: [],
      fullText: text,
      metadata: meta,
      raw,
    };

    if (result.isCurrentNote) {
      result.statusBadges.push({ label: 'Current Note', severity: 'info' });
    }

    return result;
  }

  private parseSemanticResult(item: AiSemanticSearchResult, rank: number): SemanticResult {
    return {
      rank,
      noteId: String(item.note_id ?? item.id ?? ''),
      noteTitle: String(item.title ?? ''),
      score: this.num(
        (item as any).final_score ?? (item as any).semantic_score ?? item.score ?? item.distance ?? null,
      ),
      snippet: String(item.snippet ?? ''),
      raw: item as Record<string, unknown>,
    };
  }

  private parseNormalizedTerms(value: unknown): string[] {
    const raw = String(value ?? '').trim();
    if (!raw) return [];
    return raw
      .split('|')
      .map(t => t.trim())
      .filter(t => t.length > 0);
  }

  // ── Utility helpers ────────────────────────────────────────────────────────────

  private str(value: unknown): string {
    const s = String(value ?? '').trim();
    return s === 'undefined' || s === 'null' ? '' : s;
  }

  private num(value: unknown): number | null {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private strArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.map(v => String(v));
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  }

  private readArrayField(source: Record<string, unknown>, candidates: string[]): unknown[] {
    for (const key of candidates) {
      const value = source[key];
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  // ── Template helpers ──────────────────────────────────────────────────────────

  formatScore(score: number | null): string {
    return score != null ? score.toFixed(4) : '—';
  }

  formatJson(value: unknown): string {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  warningBgClass(severity: 'warn' | 'danger' | 'info'): string {
    if (severity === 'danger') return 'bg-rose-50 border-rose-200 text-rose-800';
    if (severity === 'warn') return 'bg-amber-50 border-amber-200 text-amber-800';
    return 'bg-sky-50 border-sky-200 text-sky-800';
  }

  warningIcon(severity: 'warn' | 'danger' | 'info'): string {
    if (severity === 'danger') return 'pi pi-times-circle';
    if (severity === 'warn') return 'pi pi-exclamation-triangle';
    return 'pi pi-info-circle';
  }

  overviewSummaryBadge(): { label: string; severity: StatusBadge['severity'] } {
    if (!this.overview) return { label: 'Not loaded', severity: 'secondary' };
    if (this.overview.chunkCount === 0) return { label: 'Not indexed', severity: 'danger' };
    const hasProblems = this.warnings.some(w => w.severity === 'warn' || w.severity === 'danger');
    if (hasProblems) return { label: 'Issues found', severity: 'warn' };
    return { label: 'Healthy', severity: 'success' };
  }
}
